import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

import type CableService from './cable.ts';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import {
  initializePolicyChatRequest,
  initializeTemplateChatRequest,
} from '../requests/policy-chat.ts';

export interface PolicyChatMessage {
  id: string;
  type: 'user' | 'bot' | 'system';
  content: string;
  timestamp: Date;
  confidence?: number; // 0-1 scale
  sources?: Array<{
    section: string;
    content: string;
  }>;
}

interface HistoryEntry {
  role: 'user' | 'model';
  content: string;
}

interface IncomingMessage {
  sender?: 'user' | 'agent' | 'bot';
  id?: string;
  request_id?: string;
  content?: string;
  message?: string;
  timestamp?: string; // ISO 8601 string from ActionCable JSON serialization
  confidence?: number;
  sources?: Array<{ section: string; content: string }>;
  type?: 'history' | string;
  chunk?: string;
  message_id?: string;
  error?: string;
  session_id?: string;
  history?: HistoryEntry[];
}

// Constants
const DEFAULT_SUGGESTED_QUESTIONS = [
  'What are the eligibility requirements?',
  'What is the maximum reimbursement amount?',
  'How do I apply for tuition assistance?',
  'What documentation do I need to provide?',
  'When will I receive my reimbursement?',
] as const;

const MAX_SUGGESTIONS = 5;

export default class PolicyChatService extends Service {
  @service declare cable: CableService;
  @service declare store: StoreService;

  @tracked messages: PolicyChatMessage[] = [];
  @tracked suggestedQuestions: string[] = [];
  @tracked isConnected = false;
  @tracked isTyping = false;
  @tracked sessionId?: string;

  private subscriptionParams?: object;
  private currentStreamingMessage?: PolicyChatMessage;
  private chattableType?: string; // e.g., 'PartnerPolicy' or 'TasProgramTemplate'
  private chattableId?: string;
  private policyContext?: string; // Stored for regenerating contextual questions on clearChat
  private isInitialized = false;

  get host() {
    // @ts-expect-error - Legacy adapter method, will be migrated
    return this.store.adapterFor('application').host;
  }

  get headers() {
    // @ts-expect-error - Legacy adapter method, will be migrated
    return this.store.adapterFor('application').headers;
  }

  // ============================================================================
  // CONNECTION MANAGEMENT
  // ============================================================================

  /**
   * Connect to ActionCable for real-time responses
   * Creates a new ephemeral session each time (no localStorage persistence)
   * Note: userId parameter reserved for future user-specific features
   */
  async connect(
    _userId: string,
    chattableType: string,
    chattableId: string,
    policyContext?: string
  ): Promise<void> {
    // Store connection details
    this.chattableType = chattableType;
    this.chattableId = chattableId;
    // Only update policyContext if provided (preserve existing on reconnect)
    if (policyContext !== undefined) {
      this.policyContext = policyContext;
    }

    // Always create a new ephemeral session ID - no persistence
    this.sessionId = crypto.randomUUID();

    // Set suggested questions based on policy context, or use defaults
    if (this.messages.length === 0) {
      this.resetSuggestedQuestions();
    }

    if (this.subscriptionParams) {
      return; // Already connected
    }

    // Ensure cable is configured (should be done by app initializer)
    if (!this.cable.isConfigured) {
      console.warn('PolicyChat: Cable not configured. Waiting for app initializer.');
      return;
    }

    const channelParams = {
      channel: 'ChatChannel',
      session_id: this.sessionId, // Use session_id for chat channel
    };

    this.subscriptionParams = channelParams;
    this.cable.subscribe(channelParams, {
      connected: () => {
        this.isConnected = true;
      },

      disconnected: () => {
        this.isConnected = false;
      },

      received: (data: unknown) => {
        this.handleMessage(data as IncomingMessage);
      },
    });
  }

  /**
   * Disconnect from ActionCable
   */
  disconnect() {
    try {
      if (this.subscriptionParams) {
        this.cable.unsubscribe(this.subscriptionParams);
        this.subscriptionParams = undefined;
        this.isConnected = false;
      }
    } catch (error) {
      console.log('PolicyChat: Failed to unsubscribe from ActionCable', error);
      this.subscriptionParams = undefined;
      this.isConnected = false;
    }
  }

  // ============================================================================
  // MESSAGE HANDLING
  // ============================================================================

  /**
   * Send a question to the chat
   */
  async sendQuestion(question: string): Promise<void> {
    // Generate UUID for this message
    const requestId = crypto.randomUUID();

    // Add user message with request_id
    const userMessage: PolicyChatMessage = {
      id: requestId,
      type: 'user',
      content: question,
      timestamp: new Date(),
    };
    this.messages = [...this.messages, userMessage];

    // Show typing indicator
    this.isTyping = true;

    try {
      if (!this.isInitialized) {
        // First message: use REST API to initialize chat
        await this.initializeChatWithRestApi(question, requestId);
        this.isInitialized = true;
      } else {
        // Subsequent messages: use ActionCable
        if (!this.subscriptionParams) {
          this.isTyping = false;
          console.log('PolicyChat: No active subscription for sending message');
          this.addSystemMessage('Connection error. Please refresh and try again.');
          return;
        }

        this.cable.perform(this.subscriptionParams, 'send_message', {
          content: question,
          request_id: requestId,
          chattable_type: this.chattableType,
          chattable_id: this.chattableId,
          force: false,
        });
      }
    } catch (error) {
      console.error('PolicyChat: Failed to send question', error);
      this.isTyping = false;

      // Add error message
      this.addSystemMessage('Failed to send message. Please try again.');
    }
  }

  /**
   * Handle incoming messages from ActionCable
   * Arrow function to preserve 'this' context when used as callback
   */
  private handleMessage = (data: IncomingMessage) => {
    // Handle chat history on initial connection
    if (data.type === 'history' && data.history) {
      this.handleChatHistory(data.history);
      return;
    }

    // Handle history cleared confirmation
    if (data.type === 'history_cleared') {
      // consider removal?
      console.log('PolicyChat: Backend confirmed history cleared for session:', data.session_id);
      return;
    }

    // Handle user message echo (from backend broadcast)
    if (data.sender === 'user') {
      // Backend echoes back user messages, we already have them
      return;
    }

    // Handle bot/agent responses
    if (data.sender === 'bot') {
      this.handleBotMessage(data);
      return;
    }

    // Handle streaming chunks (future implementation)
    if (data.type === 'answer_chunk') {
      this.handleStreamingChunk(data);
      return;
    }

    // Handle streaming complete (future implementation)
    if (data.type === 'answer_complete') {
      this.handleStreamingComplete(data);
      return;
    }

    // Handle errors
    if (data.type === 'error' || data.error) {
      this.handleErrorMessage(data);
      return;
    }
  };

  /**
   * Clear chat history and reset to initial state
   * Creates a completely new ephemeral session
   */
  clearChat() {
    // Tell backend to clear Redis history for current session BEFORE creating new one
    if (this.subscriptionParams && this.sessionId) {
      try {
        const performed = this.cable.perform(this.subscriptionParams, 'clear_history', {});
        if (!performed) {
          console.warn('PolicyChat: Failed to clear history - no active subscription');
        }
      } catch {
        // Silently fail - history will be cleared on backend when session expires
        console.log('PolicyChat: Failed to clear history, will expire naturally');
      }
    }

    // Clear frontend state
    this.messages = [];
    this.isTyping = false;
    this.isInitialized = false;
    this.currentStreamingMessage = undefined;

    // Create new ephemeral session ID - no localStorage involved
    this.sessionId = crypto.randomUUID();

    // Reset suggested questions (uses stored policy context if available)
    this.resetSuggestedQuestions();

    // Reconnect with new session ID
    this.disconnect();
    if (this.chattableType && this.chattableId) {
      this.connect('', this.chattableType, this.chattableId)
        .then(() => {
          // Mark as initialized since we have an active subscription
          // This ensures subsequent messages use ActionCable instead of REST API
          this.isInitialized = true;
        })
        .catch((error) => {
          console.log('PolicyChat: Failed to reconnect:', error);
        });
    }
  }

  // ============================================================================
  // SUGGESTED QUESTIONS
  // ============================================================================

  /**
   * Reset suggested questions based on stored policy context
   * Falls back to defaults if no policy context is available
   */
  private resetSuggestedQuestions() {
    if (this.policyContext) {
      try {
        this.suggestedQuestions = this.generateSuggestionsFromPolicy(this.policyContext);
      } catch (error) {
        console.log('PolicyChat: Failed to generate suggestions', error);
        this.suggestedQuestions = [...DEFAULT_SUGGESTED_QUESTIONS];
      }
    } else {
      this.suggestedQuestions = [...DEFAULT_SUGGESTED_QUESTIONS];
    }
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  /**
   * Initialize chat session using REST API (first message only)
   */
  private async initializeChatWithRestApi(message: string, requestId: string): Promise<void> {
    if (!this.chattableType || !this.chattableId) {
      console.error('PolicyChat: Chattable type/ID not set for initialization');
      this.addSystemMessage('Configuration error. Please refresh and try again.');
      this.isTyping = false;
      return;
    }

    try {
      const payload = { message, sessionId: this.sessionId!, requestId };

      if (this.chattableType === 'PartnerPolicy') {
        await this.store.request(initializePolicyChatRequest(this.chattableId, payload));
      } else if (this.chattableType === 'TasProgramTemplate') {
        await this.store.request(initializeTemplateChatRequest(this.chattableId, payload));
      } else {
        console.error(`PolicyChat: Unknown chattable type: ${this.chattableType}`);
        this.addSystemMessage('Configuration error. Please refresh and try again.');
        this.isTyping = false;
        return;
      }

      // Backend will broadcast the answer via ActionCable ChatChannel
    } catch (error) {
      console.error('PolicyChat: Failed to initialize chat', error);
      this.addSystemMessage('Failed to connect to chat service. Please try again.');
      this.isTyping = false;
    }
  }

  /**
   * Handle bot message response
   */
  private handleBotMessage(data: IncomingMessage) {
    this.isTyping = false;

    const botMessage: PolicyChatMessage = {
      id: data.id || data.request_id || `bot-${Date.now()}`,
      type: 'bot',
      content: data.content || data.message || '',
      timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
      confidence: data.confidence,
      sources: data.sources,
    };
    this.messages = [...this.messages, botMessage];

    // Messages are stored temporarily in Redis during the session
    // No localStorage persistence - fully ephemeral
  }

  /**
   * Handle chat history received on connection
   * Converts Redis history format to frontend message format
   */
  private handleChatHistory(history: HistoryEntry[]) {
    console.log('PolicyChat: Loading chat history from backend', { count: history.length });

    // Clear existing messages
    this.messages = [];

    // Convert history entries to PolicyChatMessage format
    history.forEach((entry) => {
      if (entry.role === 'user') {
        this.messages.push({
          id: `user-${Date.now()}-${Math.random()}`,
          type: 'user',
          content: entry.content,
          timestamp: new Date(),
        });
      } else if (entry.role === 'model') {
        this.messages.push({
          id: `bot-${Date.now()}-${Math.random()}`,
          type: 'bot',
          content: entry.content,
          timestamp: new Date(),
        });
      }
    });

    // Trigger reactivity
    this.messages = [...this.messages];
    this.isInitialized = true;
  }

  /**
   * Handle streaming chunk (prepared for future backend implementation)
   */
  private handleStreamingChunk(data: IncomingMessage) {
    if (!this.currentStreamingMessage) {
      this.currentStreamingMessage = {
        id: data.message_id || data.request_id || `bot-${Date.now()}`,
        type: 'bot',
        content: data.chunk || data.content || '',
        timestamp: new Date(),
      };
      this.messages = [...this.messages, this.currentStreamingMessage];
    } else {
      this.currentStreamingMessage.content += data.chunk || data.content || '';
      this.messages = [...this.messages]; // Trigger reactivity
    }
  }

  /**
   * Handle streaming complete (prepared for future backend implementation)
   */
  private handleStreamingComplete(data: IncomingMessage) {
    this.isTyping = false;
    if (this.currentStreamingMessage) {
      this.currentStreamingMessage.confidence = data.confidence;
      this.currentStreamingMessage.sources = data.sources;
      this.messages = [...this.messages]; // Trigger reactivity
      this.currentStreamingMessage = undefined;
    }
  }

  /**
   * Handle error message
   */
  private handleErrorMessage(data: IncomingMessage) {
    this.isTyping = false;
    const errorContent = data.message || data.error || 'Something went wrong';
    this.addSystemMessage(`Error: ${errorContent}`);
  }

  /**
   * Add a system message to the chat
   */
  private addSystemMessage(content: string) {
    const systemMessage: PolicyChatMessage = {
      id: `system-${Date.now()}`,
      type: 'system',
      content,
      timestamp: new Date(),
    };
    this.messages = [...this.messages, systemMessage];
  }

  /**
   * Generate suggested questions based on policy content
   * Uses regex patterns for efficient single-pass analysis
   */
  private generateSuggestionsFromPolicy(policyContext: string): string[] {
    const suggestions: string[] = [];

    // Define patterns with associated questions (order determines priority)
    const patterns = [
      {
        regex: /\b(eligible|eligibility)\b/i,
        question: 'What are the eligibility requirements?',
      },
      {
        regex: /\b(course|program)\b/i,
        question: 'Which courses or programs are covered?',
      },
      {
        regex: /\b(reimburs|payment)\b/i,
        question: 'How does the reimbursement process work?',
      },
      {
        regex: /\b(deadline|timeline)\b/i,
        question: 'What are the important deadlines?',
      },
      {
        regex: /\b(document|proof)\b/i,
        question: 'What documentation do I need to provide?',
      },
      {
        regex: /\b(grade|score|gpa)\b/i,
        question: 'What grade requirements must I meet?',
      },
      {
        regex: /\b(limit|maximum|cap)\b/i,
        question: 'Are there any limits or caps on benefits?',
      },
      {
        regex: /\b(approval|pre-approval)\b/i,
        question: 'Do I need pre-approval before enrolling?',
      },
    ];

    // Single pass through policy content
    for (const pattern of patterns) {
      if (pattern.regex.test(policyContext)) {
        suggestions.push(pattern.question);
        // Stop once we have maximum suggestions
        if (suggestions.length >= MAX_SUGGESTIONS) {
          break;
        }
      }
    }

    // If no contextual questions found, use generic ones
    if (suggestions.length === 0) {
      suggestions.push(
        'How does this education benefit work?',
        'What are the main requirements?',
        'How do I get started?'
      );
    }

    return suggestions;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'policy-chat': PolicyChatService;
  }
}
